import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import {
  fetchCombinedSummary,
  fetchReceiveItemsByPo,
  fetchIssueItemsByWo,
  exportCombinedHistory,
  exportReceiveItemsByPo,
  exportIssueItemsByWo,
} from "../model/IssueReceiveModel";
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";

// SAP-style Purchase Order numbers: starts with 45, 10 digits total.
const PO_PATTERN = /^45\d{8}$/;
const isPoNumber = (value) => PO_PATTERN.test(value.trim());

// SAP-style Reservation/Work Order numbers: starts with 4, 7 digits total.
// Lengths never overlap with PO_PATTERN (10 digits), so there's no ambiguity.
const WORK_ORDER_PATTERN = /^4\d{6}$/;
const isWorkOrderNumber = (value) => WORK_ORDER_PATTERN.test(value.trim());
const isAutoDetectable = (value) => {
  const trimmed = value.trim();
  return PO_PATTERN.test(trimmed) || WORK_ORDER_PATTERN.test(trimmed);
};

// How long the value must sit at a qualifying exact length before we
// auto-submit — long enough that typing a 10-digit PO through its 7-digit
// (4-prefixed) intermediate state doesn't get mistaken for a completed work
// order number, short enough that a barcode scanner's near-instant
// type-then-Enter still feels immediate.
const AUTO_SUBMIT_DELAY_MS = 2000;

const ALL_MONTHS = "";

// Year dropdown is a fixed range, not derived from existing data — so it
// still shows the current year (with no data yet) instead of being empty,
// and automatically includes next year once the calendar rolls over.
const START_YEAR = 2021;
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const list = [];
  for (let year = currentYear; year >= START_YEAR; year -= 1) {
    list.push(year);
  }
  return list;
};

const IssueReceiveVm = () => {
  const navigate = useNavigate();
  const { handleToastOpen } = useToast();
  const [docNumber, setDocNumber] = useState("");
  const [loading, setLoading] = useState(false);
  // Shown when a submitted document number matches neither the PO nor the
  // Work Order format, so we can't tell which form to open automatically.
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [years] = useState(getYearOptions);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  // Default filter is the current month, not "All Months" — the user can widen it from there.
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [combinedList, setCombinedList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Drill-down: "summary" shows one row per document, "detail" shows that
  // document's material lines.
  const [historyView, setHistoryView] = useState("summary");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailList, setDetailList] = useState([]);
  const [materialNo, setMaterialNo] = useState("")
  const [materialData, setMaterialData] = useState([])
  const [locationData, setLocationData] = useState([])
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const fetchCombined = async (year, month) => {
    setLoading(true);
    try {
      const response = await fetchCombinedSummary(year, month);
      setCombinedList(response.data);
    } catch (error) {
      setCombinedList([]);
      // "Data not found!" is the expected empty-result case — anything else
      // is a real failure and was previously being hidden as if the list
      // were just empty. Surface it so we can actually see what's wrong.
      if (error.message !== "Data not found!") {
        handleToastOpen("error", `History load failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (row) => {
    setLoading(true);
    try {
      const response =
        row.doc_type === "RECEIVE"
          ? await fetchReceiveItemsByPo(row.document_no)
          : await fetchIssueItemsByWo(row.document_no);
      setDetailList(response.data);
      setSelectedDoc(row);
      setHistoryView("detail");
    } catch (error) {
      setDetailList([]);
      if (error.message !== "Data not found!") {
        handleToastOpen("error", `Detail load failed: ${error.message}`);
      } else {
        setSelectedDoc(row);
        setHistoryView("detail");
      }
    } finally {
      setLoading(false);
    }
  };

  const backToSummary = () => {
    setHistoryView("summary");
    setSelectedDoc(null);
    setDetailList([]);
  };

  useEffect(() => {
    fetchCombined(selectedYear, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedMonth(ALL_MONTHS);
    backToSummary();
    fetchCombined(year, ALL_MONTHS);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    backToSummary();
    fetchCombined(selectedYear, month);
  };

  const handleExport = async () => {
    // A 204 "no data" response can't carry a body over HTTP, so the browser
    // drops our friendly message and it'd surface as a generic error instead.
    // Catch the empty case here, before ever calling the export endpoint.
    if (combinedList.length === 0) {
      handleToastOpen("error", "No data available to export for the selected period.");
      return;
    }
    setLoading(true);
    try {
      const response = await exportCombinedHistory(selectedYear, selectedMonth);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Issue_Receive_History_${selectedYear || "all"}${selectedMonth ? `_${selectedMonth}` : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking immediately can race with the browser actually starting to
      // read the blob (especially on tablets), producing an empty/corrupt
      // download — give it a moment before releasing the object URL.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Export button on the History detail view — same download mechanics as
  // handleExport above, but scoped to just the one document currently open
  // instead of the whole year/month range.
  const handleExportDetail = async () => {
    if (!selectedDoc || detailList.length === 0) {
      handleToastOpen("error", "No data available to export for this document.");
      return;
    }
    setLoading(true);
    try {
      const response =
        selectedDoc.doc_type === "RECEIVE"
          ? await exportReceiveItemsByPo(selectedDoc.document_no)
          : await exportIssueItemsByWo(selectedDoc.document_no);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const prefix = selectedDoc.doc_type === "RECEIVE" ? "Receive" : "Issue";
      a.download = `${prefix}_${selectedDoc.document_no}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking immediately can race with the browser actually starting to
      // read the blob (especially on tablets), producing an empty/corrupt
      // download — give it a moment before releasing the object URL.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  // The PO/Work Order number is carried as a URL query param (not router
  // state) so it — and the record it resolves to — survive a page refresh;
  // ReceiveVm/IssueVm look the number up themselves on mount.
  const proceedAsReceive = (value) => {
    navigate(`/warehouse/issue-receive/receive?po_no=${encodeURIComponent(value)}`);
  };

  const proceedAsIssue = (value) => {
    navigate(`/warehouse/issue-receive/issue?work_order_no=${encodeURIComponent(value)}`);
  };


const searchMaterial = async (value)=>{
   // console.log("logging material no ");
  // console.log(materialNo);
  const Base_URL = process.env.REACT_APP_BE_URL;
  // const value = matValue ?? materialNo;

  if (!value) {
    handleToastOpen("error", "Please enter a material number");
    return;
  }

  let fetchedMaterialData = [];
  let fetchedLocationData = [];
  let hadError = false;

  try {
    const response = await AxiosWrapper.axiosGetCallWithToken(
      `${Base_URL}/gsone/getSparePartDetails?material=${value}`
    );

    if (Array.isArray(response?.data)) {
      fetchedMaterialData = response.data;
    } else {
      // backend returned an error/message object instead of an array
      fetchedMaterialData = [];
    }
  } catch (error) {
    console.log("error searching material", error);
    handleToastOpen("error", "Error fetching material details");
    hadError = true;
  }

  try {
    const locations = await AxiosWrapper.axiosGetCallWithToken(
      `${Base_URL}/gsone/binLocations?material=${value}`
    );

    if (Array.isArray(locations?.data)) {
      fetchedLocationData = locations.data;
    } else {
      fetchedLocationData = [];
    }
  } catch (error) {
    console.log("Error fetching locations", error);
    handleToastOpen("error", "Error fetching locations");
    hadError = true;
  }

  setMaterialData(fetchedMaterialData);
  setLocationData(fetchedLocationData);

  if (fetchedMaterialData.length === 0 && fetchedLocationData.length === 0) {
    if (!hadError) {
      handleToastOpen("error", "Material not found");
    }
    setShowMaterialModal(false);
    return;
  }

  setShowMaterialModal(true);
}
const handelMaterialSearch = async () => {
      searchMaterial(materialNo)
};

const isMaterialNumber = (value) =>
  /^[15]\d+$/.test(value) && [5, 6, 8].includes(value.length);

  const handleDocSubmit = () => {
    const value = docNumber.trim();
    if (!value) {
      handleToastOpen("error", "Please enter a PO or Work Order number.");
      return;
    }

    if (isMaterialNumber(value)) {
    setMaterialNo(value);
    setDocNumber("");

    setTimeout(() => {
      document.getElementById("material_number_input")?.focus();
    }, 0);

    searchMaterial(value); // or just handelMaterialSearch() if it reads state
    return;
  }

    if (isPoNumber(value)) {
      proceedAsReceive(value);
    } else if (isWorkOrderNumber(value)) {
      proceedAsIssue(value);
    } else {
      // Doesn't match either known format — ask instead of guessing.
      setShowTypeDialog(true);
    }
  };

  const confirmReceive = () => {
    setShowTypeDialog(false);
    proceedAsReceive(docNumber.trim());
  };

  const confirmIssue = () => {
    setShowTypeDialog(false);
    proceedAsIssue(docNumber.trim());
  };

  const dismissTypeDialog = () => setShowTypeDialog(false);

  // Auto-submit as soon as the typed/scanned value exactly matches a known
  // PO (10-digit, 45-prefixed) or Work Order (7-digit, 4-prefixed) format —
  // no click or Enter needed. Debounced so that typing a 10-digit PO
  // through its 7-digit intermediate state doesn't get mistaken for a
  // completed Work Order number and navigate away early.
  useEffect(() => {
    if (!isAutoDetectable(docNumber) || loading) {
      return;
    }
    const timer = setTimeout(() => {
      handleDocSubmit();
    }, AUTO_SUBMIT_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docNumber, loading]);

  // Free-text search across the summary table — filters whatever is already
  // loaded for the current Year/Month, no extra server round-trip.
  const SEARCHABLE_FIELDS = [
    "document_no",
    "delivered_by",
    "received_by",
    "issued_to",
    "collected_by",
  ];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredList = normalizedSearch
    ? combinedList.filter((row) =>
      SEARCHABLE_FIELDS.some((field) =>
        (row[field] || "").toString().toLowerCase().includes(normalizedSearch)
      )
    )
    : combinedList;

  return {
    docNumber,
    setDocNumber,
    handleDocSubmit,
    showTypeDialog,
    confirmReceive,
    confirmIssue,
    dismissTypeDialog,
    loading,
    years,
    selectedYear,
    handleYearChange,
    selectedMonth,
    handleMonthChange,
    combinedList: filteredList,
    searchTerm,
    setSearchTerm,
    handleExport,
    handleExportDetail,
    historyView,
    selectedDoc,
    detailList,
    viewDetail,
    backToSummary,
    setMaterialNo,
    materialNo,
    handelMaterialSearch,
    materialData,
    locationData,
    showMaterialModal,
    setShowMaterialModal,

  };
};

export default IssueReceiveVm;
